// RuuviTag adapter — Data Format 5 decoder.
// Spec: https://docs.ruuvi.com/communication/bluetooth-advertisements/data-format-5-rawv2
//
// Company id: 0x0499 (Ruuvi Innovations Ltd.)
// Payload (24 bytes starting after company id):
//   [0]       data format (must be 5)
//   [1..2]    temperature  (int16 BE, 0.005 C per LSB)
//   [3..4]    humidity     (uint16 BE, 0.0025 %/LSB, 0..163.835)
//   [5..6]    pressure     (uint16 BE, Pa - 50000)
//   [7..8]    accel X      (int16 BE, mg)
//   [9..10]   accel Y      (int16 BE, mg)
//   [11..12]  accel Z      (int16 BE, mg)
//   [13..14]  power info   (11 bits battery mV offset from 1600 | 5 bits tx power offset from -40)
//   [15]      movement counter (uint8)
//   [16..17]  measurement seq  (uint16 BE)
//   [18..23]  MAC address BE
//
// Invalid markers: temperature 0x8000, humidity 0xFFFF, etc — treat as null.

import type { ScanResult } from '@capacitor-community/bluetooth-le';
import {
  GenericBleProbeAdapter,
  type AdvertisingReading,
} from './generic-ble.js';
import type { ProbeVendor } from '@trackeat/shared';

const RUUVI_COMPANY_ID = 0x0499;
const RAWV2_FORMAT = 0x05;
const INVALID_I16 = -32768;
const INVALID_U16 = 0xffff;

function batteryPctFromMv(mv: number): number {
  // CR2477 discharge curve, coarse 5-point interp: 3000→100, 2800→80, 2600→50, 2400→15, 2200→0.
  if (mv >= 3000) return 100;
  if (mv >= 2800) return 80 + ((mv - 2800) / 200) * 20;
  if (mv >= 2600) return 50 + ((mv - 2600) / 200) * 30;
  if (mv >= 2400) return 15 + ((mv - 2400) / 200) * 35;
  if (mv >= 2200) return ((mv - 2200) / 200) * 15;
  return 0;
}

export class RuuviAdapter extends GenericBleProbeAdapter {
  readonly vendor: ProbeVendor = 'ruuvi';

  protected parseAdvertisement(result: ScanResult): AdvertisingReading | null {
    const bytes = this.manufacturerBytes(result, RUUVI_COMPANY_ID);
    if (!bytes || bytes.length < 24) return null;

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint8(0) !== RAWV2_FORMAT) return null;

    const tempRaw = view.getInt16(1, false);
    const humRaw = view.getUint16(3, false);
    const powerRaw = view.getUint16(13, false);

    const temperature_c = tempRaw === INVALID_I16 ? null : tempRaw * 0.005;
    const humidity_pct = humRaw === INVALID_U16 ? null : humRaw * 0.0025;

    // 11 high bits → battery offset from 1600 mV; 5 low bits → tx power offset from -40 dBm.
    const batteryBits = powerRaw >>> 5;
    const battery_mv = batteryBits === 0x7ff ? null : batteryBits + 1600;
    const battery_pct = battery_mv === null ? null : Math.round(batteryPctFromMv(battery_mv));

    if (temperature_c === null) return null;

    // Use the reported MAC from the payload when possible for a stable device id
    // (the OS-level deviceId on iOS/macOS is a rotating UUID, not a MAC).
    const macBytes = new Uint8Array(
      bytes.buffer,
      bytes.byteOffset + 18,
      6,
    );
    const mac = Array.from(macBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join(':')
      .toUpperCase();
    const deviceId = mac !== 'FF:FF:FF:FF:FF:FF' ? mac : (result.device?.deviceId ?? '');
    if (!deviceId) return null;

    return {
      reading: {
        device_id: deviceId,
        temperature_c,
        humidity_pct,
        battery_pct,
        received_at: this.nowIso(),
      },
      probe: {
        device_id: deviceId,
        name: result.device?.name ?? `Ruuvi ${mac.slice(-5)}`,
        vendor: 'ruuvi',
        model_hint: 'ruuvitag_rawv2',
        rssi: result.rssi ?? null,
        last_seen: this.nowIso(),
      },
    };
  }
}
